import React, { useEffect, useState } from 'react'
import { Button, Card, CardBody, CardHeader, Col, Row } from 'reactstrap'
import { useDebouncedCallback } from 'use-debounce'
import { Spinner } from 'reactstrap'
import { ISort } from 'lib/type'
import Select from 'react-select'
import { IDataSubmit, IEvent } from './index'
import { customStylesLabel } from 'lib/util'
import { isEqual } from 'lodash'

interface IProds {
  fetching: boolean
  booths: any[]
  events: any[]
  sortAction: ISort
  setSortAction: (arg: ISort) => void
  handleSubmit: (arg: IDataSubmit[]) => void
  searchEvent: (arg: string) => void
  setCanEdit: (arg: boolean) => void
}

const EventBoothsTable: React.FC<IProds> = ({
  booths,
  events,
  fetching,
  handleSubmit,
  sortAction,
  setSortAction,
  searchEvent,
  setCanEdit
}) => {
  const [query, setQuery] = useState('')
  const [stateEventBooths, setStateEventBooths] = useState<IEvent[]>([])
  const [initEventBooths, setInitEventBooths] = useState<any>([])
  const [disableSave, setDisableSave] = useState<boolean>(false)
  const [permissions, setPermissions] = useState({
    canCreate: false,
    canEdit: false,
    canDelete: false,
  })

  const debouncedSearch = useDebouncedCallback((searchTerm: string) => {
    searchEvent(searchTerm)
  }, 1000)

  const handleInputChange = (e: { target: { name: any; value: any } }) => {
    const value = e.target.value
    setQuery(value)
    debouncedSearch(value)
  }

  const handleActionSort = (sortBy: string) => {
    if (sortAction.sortBy === sortBy) {
      setSortAction({
        ...sortAction,
        sortDirection: sortAction.sortDirection === 'DESC' ? 'ASC' : 'DESC'
      })
    } else {
      setSortAction({
        sortDirection: 'DESC',
        sortBy: sortBy
      })
    }
  }

  useEffect(() => {
    setDisableSave(isEqual(stateEventBooths, initEventBooths))
  }, [stateEventBooths])

  useEffect(() => {
    setStateEventBooths(events)
    setInitEventBooths(events)
  }, [events])

  const handleSelect = (eventId: number, selectValues: any) => {
    setStateEventBooths(
      stateEventBooths.map((state) => {
        if (eventId === state.id) {
          return { ...state, booths: selectValues }
        }
        return state
      })
    )
  }

  const handleSave = async () => {
    const params: IDataSubmit[] = stateEventBooths.map((state: IEvent) => ({
      eventId: state.id,
      boothIds: state.booths.map((book: any) => book.value)
    }))

    handleSubmit(params)
  }

  useEffect(() => {
    const userType = sessionStorage.getItem('userType')
    
    // Type 1 = Admin - full access to everything
    if (userType === '1') {
      setPermissions({
        canCreate: true,
        canEdit: true,
        canDelete: true,
      })
      setCanEdit(true)
      return
    }
    
    // Type 2 = Regular user - use role-based permissions
    const userRoles = sessionStorage.getItem('userRoles') ? JSON.parse(atob(sessionStorage.getItem('userRoles')!)) : null
    if (userRoles) {
      userRoles.forEach((role: any) => {
        if (role.page === 'Event Booths') {
          setPermissions({
            canCreate: role.canCreate,
            canEdit: role.canEdit,
            canDelete: role.canDelete,
          })
          setCanEdit(role.canEdit)
        }
      })
    }
  }, [setCanEdit])

  return (
    <React.Fragment>
      <Row>
        <Col xl={12}>
          <Card>
            <CardHeader className='align-items-center d-flex justify-content-between'>
              <div className='search-box d-inline-block col-8 d-flex align-items-center'>
                <input
                  id='search-bar-action-price'
                  className='form-control search'
                  placeholder='Search Event'
                  onChange={(e) => handleInputChange(e)}
                  value={query}
                />
                <i className='bx bx-search-alt search-icon'></i>
              </div>
              {
                permissions.canEdit &&
                <Button className='p-2' color='primary' onClick={handleSave} disabled={disableSave}>
                  <div className='flex align-items-center'>
                    {fetching ? (
                      <Spinner size='sm'> Loading... </Spinner>
                    ) : (
                      <>
                        <span className='fw-semibold mx-3'>Save</span>
                      </>
                    )}
                  </div>
                </Button>
              }
            </CardHeader>
            <CardBody>
              <div className='table-responsive table-card overflow-visible'>
                <table className='table align-middle table-nowrap table-striped-columns mb-0'>
                  <thead className='table-light'>
                    <tr>
                      <th scope='col' onClick={() => handleActionSort('name')}>
                        <div className='w-100 d-flex justify-content-between'>
                          <span>Event</span>
                          <i
                            className={`mdi ${sortAction.sortDirection === 'ASC' && sortAction.sortBy === 'name' ? 'mdi-arrow-down-thin' : 'mdi-arrow-up-thin'}
                            ${sortAction.sortBy !== 'name' && 'opacity-25'}`}
                          ></i>
                        </div>
                      </th>
                      <th scope='col'>Booths</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stateEventBooths.map((event, index) => {
                      return (
                        <tr key={index}>
                          <td>{event.name}</td>
                          <td className='w-75'>
                            <Col col={12}>
                              <Select
                                value={event.booths}
                                isMulti={true}
                                isClearable={true}
                                onChange={(selectedMulti2: any) => {
                                  handleSelect(event.id, selectedMulti2)
                                }}
                                options={booths}
                                styles={customStylesLabel}
                                menuPortalTarget={document.body}
                                menuPosition='fixed'
                              />
                            </Col>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </React.Fragment>
  )
}
export default EventBoothsTable
